import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const DECISION_OPTIONS = [
  { value: 'For Review', label: 'For Review', badgeClass: 'bg-yellow-100 text-yellow-800' },
  { value: 'Approved', label: 'Approved', badgeClass: 'bg-green-100 text-green-800' },
  { value: 'Failed', label: 'Failed', badgeClass: 'bg-red-100 text-red-800' },
  { value: 'Excluded', label: 'Excluded', badgeClass: null },
  { value: 'Suggest Approve', label: 'Suggest Approve', badgeClass: 'bg-indigo-100 text-indigo-800' },
  { value: 'Suggest Exclude', label: 'Suggest Exclude', badgeClass: null },
  { value: 'Suggest Fail', label: 'Suggest Fail', badgeClass: 'bg-orange-100 text-orange-800' },
];

const STATUS_OPTIONS = [
  { value: 'pr1', label: 'PR1', badgeClass: 'bg-green-100 text-green-800' },
  { value: 'pr2', label: 'PR2', badgeClass: 'bg-yellow-100 text-yellow-800' },
  { value: 'pr3', label: 'PR3', badgeClass: 'bg-red-100 text-red-800' },
  { value: 'undefined', label: 'Undefined', badgeClass: null },
];

const generateMonthOptions = () => {
  const months = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const start = new Date(2025, 10); // Nov 2025
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth()); // current month
  for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
    const year = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    months.push({ value: `${year}-${mm}`, label: `${monthNames[d.getMonth()]} ${year}` });
  }
  return months.reverse();
};

const MONTH_OPTIONS = generateMonthOptions();

const CheckboxItem = ({ option, checked, onChange }) => (
  <div className="flex items-center gap-2">
    <input
      type="checkbox"
      id={`filter-${option.value}`}
      checked={checked}
      onChange={() => onChange(option.value)}
      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
    />
    <label htmlFor={`filter-${option.value}`} className="cursor-pointer">
      {option.badgeClass ? (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${option.badgeClass}`}>
          {option.label}
        </span>
      ) : (
        <span className="text-sm text-gray-700">{option.label}</span>
      )}
    </label>
  </div>
);

export const PoliciesFilter = ({ filters, onApply }) => {
  const [decisionOpen, setDecisionOpen] = useState(true);
  const [statusOpen, setStatusOpen] = useState(true);
  const [localFilters, setLocalFilters] = useState(filters);

  const hasChanged = JSON.stringify(localFilters) !== JSON.stringify(filters);

  const toggleDecision = (value) => {
    setLocalFilters(prev => ({
      ...prev,
      decision: prev.decision.includes(value)
        ? prev.decision.filter(v => v !== value)
        : [...prev.decision, value],
    }));
  };

  const toggleStatus = (value) => {
    setLocalFilters(prev => ({
      ...prev,
      status: prev.status.includes(value)
        ? prev.status.filter(v => v !== value)
        : [...prev.status, value],
    }));
  };

  const handleMonthChange = (e) => {
    setLocalFilters(prev => ({ ...prev, monthAssessed: e.target.value }));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-6">
      {/* Decision Section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-gray-900">Decision</span>
          <button
            onClick={() => setDecisionOpen(o => !o)}
            className="text-gray-500 hover:text-gray-700"
          >
            {decisionOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        {decisionOpen && (
          <div className="grid grid-cols-4 gap-x-4 gap-y-3">
            {DECISION_OPTIONS.map(opt => (
              <CheckboxItem
                key={opt.value}
                option={opt}
                checked={localFilters.decision.includes(opt.value)}
                onChange={toggleDecision}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200" />

      {/* Status Section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-gray-900">Status</span>
          <button
            onClick={() => setStatusOpen(o => !o)}
            className="text-gray-500 hover:text-gray-700"
          >
            {statusOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        {statusOpen && (
          <div className="grid grid-cols-4 gap-x-4 gap-y-3">
            {STATUS_OPTIONS.map(opt => (
              <CheckboxItem
                key={opt.value}
                option={opt}
                checked={localFilters.status.includes(opt.value)}
                onChange={toggleStatus}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200" />

      {/* Month Assessed */}
      <div className="p-4">
        <label className="block text-sm font-semibold text-gray-900 mb-2">Month Assessed:</label>
        <select
          value={localFilters.monthAssessed}
          onChange={handleMonthChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Months</option>
          {MONTH_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Apply Filters */}
      <div className="px-4 pb-4">
        <button
          onClick={() => onApply(localFilters)}
          disabled={!hasChanged}
          className={`w-full py-2.5 font-medium rounded-lg text-sm transition-colors ${
            hasChanged
              ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
              : 'bg-blue-300 text-white cursor-not-allowed opacity-50'
          }`}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};
